import {
  blankValue,
  type Metafield,
  type MetafieldValue,
  type ValueRefusal,
} from '@forecastify/shared';
import { CheckboxField, Select, TextInput, Textarea } from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import { DetailRow, INLINE_INPUT } from '../card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ PER-ENTITY FORM RENDERING: nine types, one component.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `chunks.md` asks for *"per-entity form rendering and validation"*. The rendering is this file and
 * the validation is `@forecastify/shared` — the same function the server applies, so the refusal
 * somebody sees beside an input is the refusal the API would have given.
 *
 * ⚠️ **One component for all nine types, not nine components.** A registry of per-type components
 * would be nine files that each have to remember the label, the help text, the required marker and
 * the error slot; the first one to forget the error slot is the one where a refusal disappears.
 *
 * ⚠️ **The layout rules are the mockup's `metaDesc`, measured out of it:** a textarea and a multi
 * span both columns (`sm:col-span-2`), a number renders mono, a checkbox has **no label above it**
 * because its label is beside the box, and a url gets `https://…` as its placeholder.
 */
export function MetafieldForm({
  fields,
  values,
  errors,
  members,
  onChange,
  disabled = false,
  columns = 2,
  inline = false,
}: {
  readonly fields: readonly Metafield[];
  readonly values: Readonly<Record<string, unknown>>;
  /** Keyed by the field's `key`, which is what the API's error details are keyed by too. */
  readonly errors: Readonly<Record<string, ValueRefusal>>;
  /** For the `user` type. The caller supplies them; this knows nothing about members. */
  readonly members: readonly { id: string; name: string }[];
  readonly onChange: (key: string, value: MetafieldValue) => void;
  readonly disabled?: boolean;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ HOW MANY COLUMNS — item 51, *"the metafields fill the box"*.      │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **`sm:grid-cols-2` WAS UNCONDITIONAL, AND A TAILWIND BREAKPOINT MEASURES THE VIEWPORT
   * RATHER THAN THE BOX.** So this form inside the person rail — a 308px column — got TWO columns
   * on any desktop, each about 140 pixels wide, with every control squeezed and the box's own
   * width unused around them. It looked like a rendering fault and was a media query answering a
   * question about the window when the question was about the container.
   *
   * ⚠️ **A prop rather than a container query**, because `@container` needs the ancestor to
   * declare `container-type`, which means every caller of this form opting in to a layout mode to
   * get a column count. The caller already knows how wide its box is.
   */
  readonly columns?: 1 | 2;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ A detail RAIL, not a form - and the product owner asked for this everywhere.
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **"I want every detail input field ever on any page to look like the input fields for a
   * detail row."** A rail is a list of facts that become editable under the pointer: a 92px label
   * column, a chrome-less control at 30px, a box that appears on hover. This form drew stacked
   * `Field`s - a label ABOVE a fully-chromed input - so the metafields box under a person's
   * details looked like a different product from the box above it, which is exactly what was
   * reported.
   *
   * ⚠️ **The same `DetailRow` the rails already use**, rather than a second thing that looks
   * like it. That is what makes them stay identical when one of them changes.
   */
  readonly inline?: boolean;
}) {
  const t = useT();

  if (fields.length === 0) return null;

  if (inline) {
    return (
      <>
        {fields.map((field) => {
          const value = values[field.key] ?? blankValue(field.type);
          const refusal = errors[field.key];
          const error = refusal === undefined || refusal.ok ? undefined : messageFor(t, refusal);

          return (
            <DetailRow
              key={field.id}
              label={field.name}
              {...(field.help === null ? {} : { title: field.help })}
            >
              {/* ⚠️ The refusal sits under the control rather than beside the label, because a
                  92px label column has no room for a sentence. */}
              <span className="flex min-w-0 flex-col">
                <Control
                  field={field}
                  value={value}
                  members={members}
                  disabled={disabled}
                  inline
                  onChange={onChange}
                />
                {error === undefined ? null : (
                  <span className="text-destructive px-2 text-[11px]">{error}</span>
                )}
              </span>
            </DetailRow>
          );
        })}
      </>
    );
  }

  return (
    <div className={columns === 1 ? 'grid gap-3' : 'grid gap-3 sm:grid-cols-2'}>
      {fields.map((field) => {
        /* ⚠️ `undefined` means "not in the map", which is the common case for a field nobody has
         * touched — so it becomes the TYPE's blank rather than `''`. Three different blanks; see
         * `blankValue`. */
        const value = values[field.key] ?? blankValue(field.type);
        const refusal = errors[field.key];
        const error = refusal === undefined || refusal.ok ? undefined : messageFor(t, refusal);

        /**
         * ⚠️ **A checkbox carries its label BESIDE the box**, so wrapping it in a `Field` would
         * give it two — and `Checkbox` alone renders no visible text at all, which is right in the
         * notification matrix and wrong here. `CheckboxField` is that pairing, and it lives in
         * `packages/ui` because the admin dialog wanted it next.
         */
        if (field.type === 'checkbox') {
          return (
            <CheckboxField
              key={field.id}
              checked={value === true}
              disabled={disabled}
              label={field.name}
              {...(field.help === null ? {} : { hint: field.help })}
              {...(error === undefined ? {} : { error })}
              onCheckedChange={(next) => {
                onChange(field.key, next);
              }}
            />
          );
        }

        const wide = field.type === 'textarea' || field.type === 'multi';

        /**
         * ⚠️⚠️ **A MULTI IS A GROUP, NOT A LABELLED FIELD — and this is a real accessibility bug
         * rather than a markup preference.**
         *
         * `Field` renders a `<label>`. A `<label>` labels **exactly one** control: the first
         * labelable element inside it. A multi renders one toggle button per option, so wrapping it
         * in a `<label>` makes the label name the FIRST chip — a screen reader announces chip one as
         * *"Több — nem kötelező"* and every other chip loses its own name entirely.
         *
         * Found by a test that could not locate a chip by its own text, which is exactly what a
         * screen reader could not do either. The correct markup for a set of related controls is a
         * group with an `aria-labelledby`.
         */
        if (field.type === 'multi') {
          const labelId = `mf-${field.id}-label`;

          return (
            /* ⚠️ `col-span-2` only when there ARE two — in a one-column grid it is a span past the
               end, which Grid resolves by adding an implicit column and half the form shifts. */
            <div
              key={field.id}
              className={
                columns === 1 ? 'flex flex-col gap-1.5' : 'flex flex-col gap-1.5 sm:col-span-2'
              }
            >
              <span id={labelId} className="text-[12px] font-medium">
                {field.name}
                {/* ⚠️ Item 8's inversion, hand-rolled here because this label is a `<span>` over a
                    `role="group"` rather than a `Field` — a metafield's control may be several
                    inputs, which one `<label>` cannot name. Same marker, same words. */}
                {field.required ? (
                  <>
                    <span aria-hidden="true" className="text-destructive">
                      {' *'}
                    </span>
                    <span className="sr-only"> ({t('common.required')})</span>
                  </>
                ) : null}
              </span>

              <div role="group" aria-labelledby={labelId}>
                <Control
                  field={field}
                  value={value}
                  members={members}
                  disabled={disabled}
                  onChange={onChange}
                />
              </div>

              {field.help === null ? null : (
                <span className="text-muted-foreground text-[11.5px]">{field.help}</span>
              )}
              {error === undefined ? null : (
                <span className="text-destructive text-[11.5px]">{error}</span>
              )}
            </div>
          );
        }

        return (
          /* ⚠️ Same as the `multi` branch above: a span past the end of a one-column grid adds an
             implicit column and shifts half the form sideways. */
          <div key={field.id} className={wide && columns === 2 ? 'sm:col-span-2' : undefined}>
            <Field
              label={field.name}
              /* ⚠️ The required marker goes in the LABEL, not beside the input: a hint after the
               * control is read after it by most screen readers, which is too late to be useful. */
              required={field.required}
              {...(field.help === null ? {} : { note: field.help })}
              {...(error === undefined ? {} : { error })}
            >
              <Control
                field={field}
                value={value}
                members={members}
                disabled={disabled}
                onChange={onChange}
              />
            </Field>
          </div>
        );
      })}
    </div>
  );
}

function Control({
  field,
  value,
  members,
  disabled,
  inline = false,
  onChange,
}: {
  readonly field: Metafield;
  readonly value: unknown;
  readonly members: readonly { id: string; name: string }[];
  readonly disabled: boolean;
  /** ⚠️ Chrome-less, at a rail's 30px — `MetafieldForm`'s own `inline` note argues it. */
  readonly inline?: boolean;
  readonly onChange: (key: string, value: MetafieldValue) => void;
}) {
  const t = useT();

  if (field.type === 'textarea') {
    return (
      <Textarea
        value={String(value)}
        rows={inline ? 2 : 3}
        disabled={disabled}
        {...(inline ? { className: INLINE_INPUT } : {})}
        onChange={(event) => {
          onChange(field.key, event.target.value);
        }}
      />
    );
  }

  if (field.type === 'select' || field.type === 'user') {
    /* ⚠️ The two are one control: both pick one value from a list. What differs is where the list
     * comes from — the field's own options, or the workspace's members. */
    const options =
      field.type === 'user'
        ? members.map((member) => ({ value: member.id, label: member.name }))
        : field.options.map((option) => ({ value: option, label: option }));

    return (
      <Select
        value={String(value)}
        disabled={disabled}
        inline={inline}
        /* U-14 · a person, or a workspace's own list of options — both search. */
        searchable
        searchPlaceholder={t('list.searchOptions')}
        onChange={(event) => {
          onChange(field.key, event.target.value);
        }}
        options={[{ value: '', label: t('metafields.none') }, ...options]}
      />
    );
  }

  if (field.type === 'multi') {
    /**
     * ⚠️ **Toggle chips rather than a multi-select listbox**, which is what the mockup draws — and
     * it is the better control: a native `<select multiple>` requires ctrl-clicking to deselect,
     * which essentially nobody knows, and on a phone it is unusable.
     *
     * ⚠️ Each chip is a real `<button>` with `aria-pressed`, so the state is announced. Colour
     * alone would not say which are on.
     */
    const chosen = Array.isArray(value) ? (value as string[]) : [];

    return (
      <div className="flex flex-wrap gap-1.5">
        {field.options.map((option) => {
          const on = chosen.includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              className={[
                'rounded-md border px-2 py-1 text-[12px] transition-colors',
                on ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted',
              ].join(' ')}
              onClick={() => {
                onChange(
                  field.key,
                  on ? chosen.filter((entry) => entry !== option) : [...chosen, option],
                );
              }}
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.type === 'date') {
    return (
      <DateField
        label={field.name}
        value={String(value)}
        disabled={disabled}
        {...(inline ? { buttonClassName: 'h-[30px] border-transparent shadow-none' } : {})}
        onChange={(next) => {
          onChange(field.key, next);
        }}
      />
    );
  }

  /* `text`, `number` and `url`. Mono for a number, `https://…` for a url — the mockup's own. */
  return (
    <TextInput
      value={String(value)}
      disabled={disabled}
      className={inline ? INLINE_INPUT : undefined}
      placeholder={field.type === 'url' ? 'https://…' : field.type === 'number' ? '0' : undefined}
      onChange={(event) => {
        onChange(field.key, event.target.value);
      }}
    />
  );
}

/**
 * A refusal becomes a sentence.
 *
 * ⚠️ **One `t()` per branch with a literal key** — a computed key is invisible to anything scanning
 * the source for catalogue usage, and every branch's entry then reports as an orphan.
 */
export function messageFor(t: Translate, refusal: ValueRefusal): string {
  if (refusal.ok) return '';
  if (refusal.reason === 'required') return t('metafields.error.required');
  if (refusal.reason === 'not_a_number') return t('metafields.error.number');
  if (refusal.reason === 'not_a_date') return t('metafields.error.date');
  if (refusal.reason === 'not_a_url') return t('metafields.error.url');
  if (refusal.reason === 'not_an_option') {
    return t('metafields.error.option', { value: refusal.offending });
  }
  return t('metafields.error.shape');
}

/** A field's note for a list header, so a screen can label an `in_list` column. */
export function listFields(fields: readonly Metafield[]): readonly Metafield[] {
  return fields.filter((field) => field.inList);
}

/**
 * How a stored value reads in a list cell.
 *
 * ⚠️ **An em-dash for absent, `Igen`/`Nem` for a boolean, a comma list for a multi.** A raw `false`
 * would render as nothing, which is indistinguishable from unanswered — and those are different
 * facts, which is exactly why `blankValue` keeps them apart.
 */
export function displayValue(t: Translate, field: Metafield, value: unknown): string {
  if (value === undefined || value === null || value === '') return '–';
  if (field.type === 'checkbox') return value === true ? t('common.yes') : t('common.no');
  if (Array.isArray(value)) return value.length === 0 ? '–' : value.join(', ');
  if (field.type === 'user') return t('metafields.someone');

  /**
   * ⚠️ **Narrowed rather than `String(value)`, and lint is right to insist.** `value` is `unknown`
   * here — it comes back from `jsonb` — so `String()` on an object gives `[object Object]` in a
   * table cell, which reads as corrupted data rather than as an unexpected shape.
   *
   * A value that is none of the handled kinds means the stored `jsonb` does not match its field's
   * type, which is possible only if a type was changed without discarding the values — something
   * `metafield.service.ts` deliberately does not allow. So the em-dash here is the honest answer:
   * *there is nothing displayable*, rather than a guess.
   */
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '–';
}

/** The badge the admin list shows for a type. Nine labels, one `t()` per branch. */
export function typeLabel(t: Translate, type: Metafield['type']): string {
  if (type === 'text') return t('metafields.type.text');
  if (type === 'textarea') return t('metafields.type.textarea');
  if (type === 'number') return t('metafields.type.number');
  if (type === 'date') return t('metafields.type.date');
  if (type === 'select') return t('metafields.type.select');
  if (type === 'multi') return t('metafields.type.multi');
  if (type === 'checkbox') return t('metafields.type.checkbox');
  if (type === 'user') return t('metafields.type.user');
  return t('metafields.type.url');
}

/**
 * ⚠️⚠️ **A RECORD, NOT A CHAIN OF `if`s ENDING IN A FALL-THROUGH — AND C30 IS WHY.** This was five
 * comparisons with `return t('metafields.entity.worklog')` as the else, so adding `contract` and
 * `lineitem` to the vocabulary made BOTH of them render as *Worklog*. Typecheck was green: a
 * fall-through returns a string whatever it is handed.
 *
 * A `Record<MetafieldEntity, MessageKey>` cannot be missing a member. A ninth entity would fail the
 * build instead of quietly wearing the seventh's name.
 *
 * ⚠️ **Written out rather than a computed key**: `i18n:check` reads literal
 * arguments only, and a computed key reports all eight as orphans — which somebody then deletes.
 */
const ENTITY_KEY: Readonly<Record<Metafield['entity'], MessageKey>> = {
  task: 'metafields.entity.task',
  person: 'metafields.entity.person',
  project: 'metafields.entity.project',
  client: 'metafields.entity.client',
  worklog: 'metafields.entity.worklog',
  contract: 'metafields.entity.contract',
  lineitem: 'metafields.entity.lineitem',
  /* C56 · the eighth, and this map is where the build stopped to say so. */
  external_cost: 'metafields.entity.externalCost',
  invoice: 'metafields.entity.invoice',
};

export function entityLabel(t: Translate, entity: Metafield['entity']): string {
  return t(ENTITY_KEY[entity]);
}
